import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, inject, Input, Output, WritableSignal } from '@angular/core';
import { debounceTime, distinctUntilChanged, Subject, Subscription, takeUntil, tap, map, switchMap, from } from 'rxjs';
import { DriverInformation } from '../../models/drivers.model';
import { DriverDocumentNotificationResult } from '../../../../_shared/models/drivers/management/driver-document-notification.mode';
import { AvatarComponent } from '../../../../_shared/components/avatar/avatar.component';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { DriverDocumentsService } from '../../services/driver-documents.service';
import { DriverDocumentsListService } from '../../services/driver-documents-list.service';
import { DriversListPaginationRequest } from '../../models/driver-pagination-request.model';
import { ErrorHandlerService } from '../../../../_shared/services/error-handler.service';
import { ActivatedRoute, Router } from '@angular/router';
import { DocumentStatus } from '../../../../_shared/enums/document-status';
import { DocumentNotificationResult, DriverExpiryAlert, DriverExpiryAlertRequest } from '../../models/document-expiry-alert.model';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-driver-documents-list',
  standalone: true,
  imports: [CommonModule, AvatarComponent, InfiniteScrollModule, NgbTooltipModule, TranslocoPipe],
  templateUrl: './driver-documents-list.component.html',
  styleUrl: './driver-documents-list.component.scss'
})
export class DriverDocumentsListComponent {
  @Input() hasDrivers: WritableSignal<boolean | null>;
  @Output() hasResults = new EventEmitter<boolean>();
  @Output() loaded = new EventEmitter<boolean>();
  @Output() driverId = new EventEmitter<number>();

  documentsDriversList: any[] = [];
  isLoading: boolean = false;
  hasMoreData: boolean = true;
  selectedIndex: number | null = null;
  isFirstSelected: boolean = false;
  alerts: DriverExpiryAlert[] = [];
  notificationMessage: string = '';
  notificationCount: number = 0;
  paginatedRequest: DriversListPaginationRequest = {
    driverId: null,
    accountIds: [],
    page: 1,
    pageSize: 20,
    searchTerm: ''
  };
  
  private hasDriversOnInit: boolean = false;
  
  private destroy$ = new Subject<void>();
  private cancellation = new Subject<void>();
  
  private searchSubscription: Subscription;
  private alertSubscription: Subscription;
  private routeSubscription: Subscription;
  private driverDocumentsService = inject(DriverDocumentsService);
  private driverDocumentsListService = inject(DriverDocumentsListService);
  private cdr = inject(ChangeDetectorRef);
  private errorHandlerService = inject(ErrorHandlerService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private transloco = inject(TranslocoService);

  ngOnInit(): void {
    this.subscribeToSearch();
    this.subscribeToAlerts();

    this.routeSubscription = this.route.params.pipe(
      takeUntil(this.destroy$),
      map(param => {
        const driverId = Number(param['id']);
        return Number.isNaN(driverId) || driverId <= 0 ? null : driverId;
      }),
      distinctUntilChanged(),
      tap(driverId => {
        this.paginatedRequest.driverId = driverId;
        this.documentsDriversList = [];
        this.hasMoreData = driverId ? false : true;
      }),
      switchMap(() => from(this.getDocumentsDriversList())),
      switchMap(() => from(this.refreshAlerts(null)))
    ).subscribe();
  }

  ngOnDestroy(): void {
    this.searchSubscription.unsubscribe();
    this.alertSubscription.unsubscribe();
    this.routeSubscription.unsubscribe();

    this.destroy$.next();
    this.destroy$.complete();
    this.cancellation.next();
    this.cancellation.complete();
  }

  subscribeToSearch(): void {
    this.searchSubscription = this.driverDocumentsListService.searchTerm$
      .pipe(debounceTime(1000), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(async searchTerm => {
        this.paginatedRequest.page = 1;
        this.paginatedRequest.searchTerm = searchTerm;
        this.resetPagination();

        await this.getDocumentsDriversList();
      });
  }

  subscribeToAlerts(): void {
    this.alertSubscription = this.driverDocumentsListService.refreshAlerts$.pipe(takeUntil(this.destroy$)).subscribe((id) => {
      this.refreshAlerts(id ?? null);
    });
  }

  async getDocumentsDriversList() {
    if ((this.isLoading || !this.hasMoreData) && !this.paginatedRequest.driverId) {
      return;
    }

    this.isLoading = true;
    try {
      this.cancellation.next();
      const response = await this.driverDocumentsService.getDriversList(this.paginatedRequest, this.cancellation);
      let newDrivers = this.mapDriverData(response?.data?.results ?? []);
      if (newDrivers.length && this.paginatedRequest.page > 1) {
        newDrivers = this.applyAlertsToDrivers(newDrivers, this.alerts);
      }

      this.hasResults.emit(newDrivers.length > 0);
      this.documentsDriversList = [...this.documentsDriversList, ...newDrivers];
      if (!this.documentsDriversList.length) {
        this.clearSelection();
      }

      this.driverDocumentsListService.updateDrivers(this.documentsDriversList);
      if (this.paginatedRequest.page === 1) {
        this.selectDriver(this.documentsDriversList[0], 0);
      }

      this.hasMoreData = newDrivers.length === this.paginatedRequest.pageSize;
      if (this.hasMoreData) this.paginatedRequest.page++;
      this.checkHasDrivers();
      

      this.isLoading = false;
      this.cdr.detectChanges();
      this.loaded.emit(true);
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        showToast: true,
        callback: () => {
          this.isLoading = false;
          this.cdr.detectChanges();
          this.loaded.emit(true);
        }
      });
    }
  }

  async refreshAlerts(driverId: number | null = null): Promise<void> {
    try {
      const request: DriverExpiryAlertRequest = { driverId };
      const response = await this.driverDocumentsService.getDriverDocumentExpiryAlerts(request);
      this.alerts = response?.data ?? [];

      if (!this.alerts.length) {
        if (driverId) {
          this.documentsDriversList = this.documentsDriversList.map(d =>
            d.id === driverId ? { ...d, notification: null } : d
          );
        }
        
        this.driverDocumentsListService.updateDrivers(this.documentsDriversList);
        this.cdr.detectChanges();
        return;
      }

      const alertsByDriverId = new Map(this.alerts.map(alert => [alert.driverId, alert]));
      this.documentsDriversList = this.documentsDriversList.map(d => {
        const driverAlert = alertsByDriverId.get(d.id) ?? null;
        const notification = driverAlert
          ? this.buildDriverNotification(driverAlert.expiredCount, driverAlert.expiringSoonCount)
          : (driverId === null ? null : d.notification);
  
        return { ...d, notification: notification };
      });

      this.driverDocumentsListService.updateDrivers(this.documentsDriversList);
      this.cdr.detectChanges();
    } catch (error: any) {
      this.errorHandlerService.handleError(error);
    }
  }

  showAllDrivers(): void {
    this.router.navigate(['/driver-management/driver-documents']).then(() => {
      this.paginatedRequest.driverId = null;
      this.paginatedRequest.searchTerm = '';

      this.documentsDriversList = [];
      this.selectedIndex = null;
      this.hasMoreData = true;
      this.isFirstSelected = false;

      this.driverDocumentsListService.updateSelectedDriver(null);
      this.driverDocumentsListService.updateDrivers(null);
    });
  }

  notificationHandler(driver: any): DriverDocumentNotificationResult | null {
    this.notificationMessage = driver.notification?.message ?? '';
    this.notificationCount = driver.notification?.count ?? 0;

    return driver.notification ?? null;
  }

  checkHasDrivers(): void {
    if (this.hasDrivers() === null) {
      const hasDrivers = this.documentsDriversList.length > 0;
      this.hasDrivers.set(hasDrivers);
      this.hasDriversOnInit = hasDrivers;
    } else if (this.documentsDriversList.length > 0 || (this.documentsDriversList.length === 0 && this.hasDriversOnInit)) {
      this.hasDrivers.set(true);
    } else {
      this.hasDrivers.set(false);
    }
  }

  selectDriver(driver: any | null, selectedIndex: number | null): void {
    this.selectedIndex = selectedIndex;
    if (!driver) {
      this.driverDocumentsListService.updateSelectedDriver(null);
      return;
    }

    this.driverDocumentsListService.updateSelectedDriver(driver);
  }

  onScrollDown(): void {
    if (this.paginatedRequest.driverId) return;
    this.getDocumentsDriversList();
  }

  private clearSelection(): void {
    this.driverDocumentsListService.updateSelectedDriver(null);
    this.driverDocumentsListService.updateDrivers(null);
    this.selectedIndex = null;
  }

  private mapDriverData(drivers: DriverInformation[]): any {
    return drivers.map(driver => ({
      id: driver.id,
      name: `${driver.firstName} ${driver.lastName}`,
      accountName: driver?.accountName ?? this.transloco.translate('common.unknown'),
      image: driver?.profilePictureUrl,
      notification: null as DocumentNotificationResult | null
    }));
  }

  private applyAlertsToDrivers(drivers: any, alerts: DriverExpiryAlert[]): any[] {
    const alertsByDriverId = new Map(alerts.map(alert => [alert.driverId, alert]));  
    return drivers.map((d: any) => {
      const driverAlert = alertsByDriverId.get(d.id) ?? null;
      const notification = driverAlert
        ? this.buildDriverNotification(driverAlert.expiredCount, driverAlert.expiringSoonCount)
        : (d.notification) ?? null;

      return { ...d, notification: notification };
    });
  }

  private buildDriverNotification(expiredCount: number, expiringSoonCount: number): DocumentNotificationResult | null {
    const count = (expiredCount || 0) + (expiringSoonCount || 0);
  
    if (count <= 0) return null;
  
    let message = '';
    if (expiredCount > 0 && expiringSoonCount > 0) {
      message = this.transloco.translate(
        expiredCount > 1
          ? 'driverDocuments.notifications.expiringAndExpiredPlural'
          : 'driverDocuments.notifications.expiringAndExpired',
        { expiring: expiringSoonCount, expired: expiredCount });
    } else if (expiredCount > 0) {
      message = this.transloco.translate(
        expiredCount > 1
          ? 'driverDocuments.notifications.expiredPlural'
          : 'driverDocuments.notifications.expired',
        { count: expiredCount });
    } else if (expiringSoonCount > 0) {
      message = this.transloco.translate(
        expiringSoonCount > 1
          ? 'driverDocuments.notifications.expiringSoonPlural'
          : 'driverDocuments.notifications.expiringSoon',
        { count: expiringSoonCount });
    }
  
    const status: DocumentStatus[] = [];
    if (expiredCount > 0) status.push(DocumentStatus.Expired);
    if (expiringSoonCount > 0) status.push(DocumentStatus.ExpiringSoon);
  
    return { count, message, status };
  }

  private resetPagination(): void {
    this.paginatedRequest.page = 1;
    this.hasMoreData = true;
    this.documentsDriversList = [];
    this.selectedIndex = null;
  }
}